/**
 * Composición del fondo: degradado, halos, trama, viñeta y grano, más la
 * medición de contraste. renderBackground es puro respecto a React y sirve
 * igual en el navegador que en un script con node-canvas.
 */
import { contrast, css, mix, ramp, relLum, rng32 } from './color';
import { buildPalette } from './policies';
import { paintPattern } from './patterns';
import type {
  AuditEntry, BackgroundConfig, Ctx, ImageFormat, Palette, RenderResult, RGB, Rng
} from './types';

function paintGradient(g: Ctx, W: number, H: number, P: Palette, cfg: BackgroundConfig, rnd: Rng) {
  const stops = ramp(P.dom, P.sec, 18);
  const a = cfg.angle * Math.PI / 180;
  const spread = cfg.spread;

  if (cfg.gtype === 'linear' || cfg.gtype === 'bands') {
    const cx = W / 2, cy = H / 2, r = (Math.abs(Math.cos(a)) * W + Math.abs(Math.sin(a)) * H) / 2 * spread;
    const grad = g.createLinearGradient(cx - Math.cos(a) * r, cy - Math.sin(a) * r, cx + Math.cos(a) * r, cy + Math.sin(a) * r);
    if (cfg.gtype === 'bands') {
      const n = 7, band = ramp(P.dom, P.acc, n);
      band.forEach((c, i) => { grad.addColorStop(i / n, css(c)); grad.addColorStop((i + 1) / n - 0.0001, css(c)); });
    } else {
      stops.forEach((c, i) => grad.addColorStop(i / (stops.length - 1), css(c)));
    }
    g.fillStyle = grad; g.fillRect(0, 0, W, H);

  } else if (cfg.gtype === 'radial') {
    g.fillStyle = css(P.sec); g.fillRect(0, 0, W, H);
    const cx = W * (0.5 + Math.cos(a) * 0.22), cy = H * (0.5 + Math.sin(a) * 0.22);
    const grad = g.createRadialGradient(cx, cy, 0, cx, cy, Math.hypot(W, H) * 0.62 * spread);
    stops.forEach((c, i) => grad.addColorStop(i / (stops.length - 1), css(c)));
    g.fillStyle = grad; g.fillRect(0, 0, W, H);

  } else if (cfg.gtype === 'conic') {
    const cx = W / 2, cy = H / 2, R = Math.hypot(W, H);
    const N = 240, full = ramp(P.dom, P.sec, N / 2).concat(ramp(P.sec, P.dom, N / 2));
    for (let i = 0; i < N; i++) {
      const a0 = a + (i / N) * Math.PI * 2, a1 = a + ((i + 1.6) / N) * Math.PI * 2;
      g.beginPath(); g.moveTo(cx, cy); g.arc(cx, cy, R, a0, a1); g.closePath();
      g.fillStyle = css(full[i]!); g.fill();
    }
  } else { /* mesh */
    g.fillStyle = css(P.dom); g.fillRect(0, 0, W, H);
    const blobs = [P.sec, P.acc, mix(P.dom, P.light, 0.35), mix(P.dom, P.sec, 0.6), P.sec];
    blobs.forEach((c, i) => {
      const cx = W * (0.15 + rnd() * 0.7), cy = H * (0.15 + rnd() * 0.7);
      const r = Math.hypot(W, H) * (0.16 + rnd() * 0.26) * spread;
      const grad = g.createRadialGradient(cx, cy, 0, cx, cy, r);
      const [R0, G0, B0] = c.rgb;
      /* peso decreciente: el 60/30/10 se mantiene también en área */
      grad.addColorStop(0, `rgba(${R0},${G0},${B0},${i < 2 ? 0.85 : 0.5})`);
      grad.addColorStop(0.55, `rgba(${R0},${G0},${B0},0.32)`);
      grad.addColorStop(1, `rgba(${R0},${G0},${B0},0)`);
      g.fillStyle = grad; g.fillRect(0, 0, W, H);
    });
  }
}

function paintGlow(g: Ctx, W: number, H: number, P: Palette, rnd: Rng) {
  const src = [P.light, P.acc, P.light];
  src.forEach((c, i) => {
    const cx = W * (0.1 + rnd() * 0.8), cy = H * (0.1 + rnd() * 0.8);
    const r = Math.hypot(W, H) * (0.12 + rnd() * 0.2);
    const grad = g.createRadialGradient(cx, cy, 0, cx, cy, r);
    const [R, G, B] = c.rgb;
    grad.addColorStop(0, `rgba(${R},${G},${B},${0.16 - i * 0.03})`);
    grad.addColorStop(1, `rgba(${R},${G},${B},0)`);
    g.fillStyle = grad; g.fillRect(0, 0, W, H);
  });
}

function paintVignette(g: Ctx, W: number, H: number, P: Palette) {
  const grad = g.createRadialGradient(W / 2, H / 2, Math.min(W, H) * 0.28, W / 2, H / 2, Math.hypot(W, H) * 0.66);
  const [R, G, B] = P.dark.rgb;      /* sombra teñida, nunca negro puro */
  grad.addColorStop(0, `rgba(${R},${G},${B},0)`);
  grad.addColorStop(1, `rgba(${R},${G},${B},0.42)`);
  g.fillStyle = grad; g.fillRect(0, 0, W, H);
}

function paintGrain(g: Ctx, W: number, H: number, rnd: Rng) {
  const s = 3, w = Math.ceil(W / s), h = Math.ceil(H / s);
  const off = document.createElement('canvas'); off.width = w; off.height = h;
  const oc = off.getContext('2d')!, img = oc.createImageData(w, h);
  for (let i = 0; i < img.data.length; i += 4) {
    const v = 110 + rnd() * 90;
    img.data[i] = img.data[i + 1] = img.data[i + 2] = v; img.data[i + 3] = 255;
  }
  oc.putImageData(img, 0, 0);
  g.save(); g.globalCompositeOperation = 'overlay'; g.globalAlpha = 0.14;
  g.imageSmoothingEnabled = true;
  g.drawImage(off, 0, 0, W, H); g.restore();
}

function averageLuminance(cv: HTMLCanvasElement): number {
  const off = document.createElement('canvas'); off.width = 24; off.height = 14;
  const oc = off.getContext('2d')!;
  oc.drawImage(cv, 0, 0, 24, 14);
  const d = oc.getImageData(0, 0, 24, 14).data;
  let sum = 0, n = 0;
  for (let i = 0; i < d.length; i += 4) { sum += relLum([d[i], d[i + 1], d[i + 2]] as RGB); n++; }
  return sum / n;
}

export const DEFAULT_CONFIG: BackgroundConfig = {
  seed: 481207,
  hue: 248,
  chroma: 0.14,
  light: 0.42,
  harmony: 'analogous',
  gtype: 'linear',
  angle: 135,
  spread: 0.7,
  pattern: 'dots',
  pscale: 36,
  popacity: 0.18,
  blend: 'soft-light',
  vignette: true,
  glow: true,
  grain: false,
  width: 1600,
  height: 900
};

export function renderBackground(canvas: HTMLCanvasElement, config: Partial<BackgroundConfig> = {}): RenderResult {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  const W = cfg.width, H = cfg.height;
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;

  const P = buildPalette(cfg);
  const rnd = rng32(cfg.seed ^ 0x9E3779B9);

  paintGradient(ctx, W, H, P, cfg, rnd);
  if (cfg.glow) paintGlow(ctx, W, H, P, rnd);

  /* P-07 · La tinta de la trama sale siempre de la paleta, nunca blanco
     ni negro puros, y se elige por la luminosidad del soporte. */
  paintPattern(ctx, W, H, P.dom.L > 0.55 ? P.dark : P.light, cfg, rnd);

  if (cfg.vignette) paintVignette(ctx, W, H, P);
  if (cfg.grain) paintGrain(ctx, W, H, rnd);

  /* P-08 · Contraste WCAG medido sobre la luminancia media real del lienzo */
  const avg = averageLuminance(canvas);
  const grey: RGB = [Math.round(avg * 255), Math.round(avg * 255), Math.round(avg * 255)];
  const textColor = contrast(P.light.rgb, grey) >= 4.5 ? P.light : P.dark;
  const ratio = (Math.max(relLum(textColor.rgb), avg) + 0.05) / (Math.min(relLum(textColor.rgb), avg) + 0.05);

  const audit: AuditEntry[] = [
    ...P.log,
    {
      state: ratio >= 4.5 ? 'ok' : ratio >= 3 ? 'fix' : 'warn',
      title: 'Contraste del texto (WCAG)',
      detail: ratio >= 4.5
        ? `${ratio.toFixed(2)}:1 con la tinta ${textColor.hex}. Cumple AA para texto normal.`
        : ratio >= 3
          ? `${ratio.toFixed(2)}:1. Solo válido para texto grande (AA large ≥ 3:1). Baja la intensidad de la trama o separa más la luminosidad.`
          : `${ratio.toFixed(2)}:1. Insuficiente: este fondo necesita una capa de velado bajo el texto.`
    },
    {
      state: 'ok',
      title: 'Reparto 60 / 30 / 10',
      detail: `${P.dom.hex} ocupa el soporte, ${P.sec.hex} modula la mezcla y ${P.acc.hex} queda reservado a acentos y halos.`
    }
  ];

  return { config: cfg, palette: P, audit, textColor, ratio, avgLuminance: avg };
}

/** Data URL del lienzo más su peso real en KB (el Base64 abulta un 33 %). */
export function exportCanvas(canvas: HTMLCanvasElement, format: ImageFormat = 'image/png', quality = 0.82) {
  const url = format === 'image/png' ? canvas.toDataURL(format) : canvas.toDataURL(format, quality);
  const kb = (url.length - url.indexOf(',') - 1) * 3 / 4 / 1024;
  return { url, kb };
}
